import { AbilityBuilder, createMongoAbility } from "@casl/ability";
import type { ActionFor, AppSubjectName } from "./strategies/resource-map";
import type { AppAbility } from "./types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PermissionUser {
	id: string;
	orgId: string;
	isOwner: boolean; // derived from organizationMembers.role === 'owner'
	projectIds: string[]; // projects this user is a member of (projectMembers)
}

// A distributed union over the manifest rather than a flat
// `{ action: Action; subject: string }`, so an action a subject does not
// declare is a compile error at the point the rule is written — which is how
// `read Plan` sat off-manifest while the map allowed only `update`.
//
// Rows loaded from userPermissions arrive as plain strings and are cast at the
// database boundary; that cast is the one place the pairing is unchecked.
export type PermissionRow = {
	[S in AppSubjectName]: {
		action: ActionFor<S>;
		subject: S;
		conditions?: Record<string, unknown> | null;
	};
}[AppSubjectName];

// Where a resolved permission came from. A list rather than a single value,
// because the model is additive: two roles may grant the same rule, and an
// individual grant may sit on top of a role that already carries it. Reporting
// only one of them would tell an admin that removing it is enough, which is
// exactly the mistake this shape exists to prevent.
export type PermissionSource =
	| { kind: "role"; roleId: string; roleName: string; isSystem: boolean }
	| { kind: "individual" };

// A permission as the API reports it: the rule itself, plus everything that
// grants it. Assignable wherever a PermissionRow is expected, so the ability
// builder consumes it unchanged and ignores the extra field.
export type ResolvedPermission = PermissionRow & { sources: PermissionSource[] };

// ---------------------------------------------------------------------------
// Condition interpolation
// Replaces ${user.x} placeholders with actual user values at runtime.
// e.g. {"orgId": "${user.orgId}"} → {"orgId": "org_123"}
//
// Two forms, and the distinction matters:
//
//   whole    "${user.projectIds}"       → the raw claim, array and all
//   embedded "project-${user.id}"       → string substitution, as before
//
// Only the whole form can yield a non-string, which is what lets a rule gate
// on project membership: {"projectId": {"$in": "${user.projectIds}"}}. String
// substitution would coerce the array to "a,b,c" and match nothing.
//
// Interpolation recurses through nested objects and arrays, because CASL's
// query operators ($in, $ne, …) put the placeholder one level below the key.
// ---------------------------------------------------------------------------

function getClaim(user: PermissionUser, key: string): unknown {
	return Object.hasOwn(user, key) ? user[key as keyof PermissionUser] : undefined;
}

function interpolateValue(value: unknown, user: PermissionUser): unknown {
	if (typeof value === "string") {
		const whole = /^\$\{user\.(\w+)\}$/.exec(value);
		if (whole?.[1]) {
			const claim = getClaim(user, whole[1]);
			// An unknown claim leaves the placeholder untouched, so the rule
			// fails closed rather than matching some coerced value.
			return claim === undefined ? value : claim;
		}

		return value.replace(/\$\{user\.(\w+)\}/g, (match, key: string) => {
			const claim = getClaim(user, key);
			return typeof claim === "string" ? claim : match;
		});
	}

	if (Array.isArray(value)) {
		return value.map((entry) => interpolateValue(entry, user));
	}

	if (value !== null && typeof value === "object") {
		return Object.fromEntries(
			Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
				key,
				interpolateValue(entry, user),
			]),
		);
	}

	return value;
}

function interpolate(
	conditions: Record<string, unknown>,
	user: PermissionUser,
): Record<string, unknown> {
	return interpolateValue(conditions, user) as Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// buildAbility
// The model is purely additive: every row is a grant, and a caller's abilities
// are the union of all of them. Nothing takes anything away, so the order the
// rows arrive in does not decide any check — two rules for the same pair widen
// each other rather than compete.
// 1. owner  → inject manage all (no DB rows needed)
// 2. roles  → the permissions of every role the user carries
// 3. user   → individual grants on top
// ---------------------------------------------------------------------------

export function buildAbility(user: PermissionUser, permissions: PermissionRow[]): AppAbility {
	const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

	// Owner always gets full access — no DB rows needed
	if (user.isOwner) {
		can("manage", "all");
		return build();
	}

	for (const perm of permissions) {
		const conditions = perm.conditions ? interpolate(perm.conditions, user) : undefined;
		can(perm.action, perm.subject as unknown as string, conditions);
	}

	return build();
}
